import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";

import {
	Box,
	Button,
	Typography,
	TextField,
	IconButton,
	Grid,
	Tooltip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Autocomplete,
	CircularProgress,
	Checkbox,
} from "@mui/material";

import {
	DatePicker,
	LocalizationProvider,
	TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useAppDispatch, useAppSelector } from "../../store/hooks.ts";

import {
	selectSelectedSession,
	clearSelectedSession,
	setSessionEditMode,
	updateSessionField,
	resetSessionEdits,
	saveSessionEdits,
	selectOriginalSession,
	setSelectedSession,
} from "../../store/slices/sessionSlice.ts";

import { setSelectedEvent } from "../../store/slices/eventSlice.ts";

import ListboxComponent from "../../components/listbox/ListBox.tsx";
import { SERVER_URL } from "../../config.js";
import globalAxios from "../../globalAxios.js";
import {
	calculateEndTime,
	setEventColor,
} from "../calendar/helper/eventRendering.tsx";
import {
	removeSession,
	setSelectedEntityType,
	updateEvent,
	updateSession,
} from "../../store/slices/calendarSlice.ts";
import {
	setSnackbarOpen,
	setSnackbarSeverity,
	setSnackbarText,
} from "../../store/slices/snackbarSlice.ts";

import "./SessionView.css";
import { playDelete, playSuccessSave } from "../../components/sounds/index.ts";
import {
	calculateFeeEstimate,
	calculateSinglePtFeeEstimate,
} from "../calendar/helper/feeCalculation.ts";
import { selectUserSettings } from "../../store/slices/userSlice.ts";
import {
	fetchSchedulesIfNeeded,
	upsertSchedules,
} from "../../store/slices/schedulesSlice.ts";
import { extractItemNumbersFromRedux } from "../../utils/itemNumberUtils.ts";
import {
	setLoadingDetail,
	setSelectedItem,
} from "../../store/slices/searchSlice.ts";
import { useNavigate } from "react-router-dom";
import { equalMoney, normaliseCurrency } from "../../utils/oopUtils.ts";
import { resolveSurgeonBillingRule } from "../calendar/helper/surgeonBillingRules.ts";
import { setSurgeonBillingRulesForSurgeon } from "../../store/slices/surgeonBillingRulesSlice.ts";

const SessionView = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const userSettings = useAppSelector(selectUserSettings);
	const schedules = useAppSelector((state) => state.schedules.data);
	const validItemNumbers = useMemo(
		() => new Set(schedules.map((s) => String(s.item_num))),
		[schedules],
	);

	const session = useAppSelector(selectSelectedSession);
	const originalSession = useAppSelector(selectOriginalSession);

	const surgeonBillingRulesBySurgeonId = useAppSelector(
		(state) => state.surgeonBillingRules.rulesBySurgeonId,
	);

	const billingSurgeonId = useMemo(() => {
		const rawSurgeonId = session?.surgeon_id ?? session?.surgeon?.id;

		if (rawSurgeonId === null || rawSurgeonId === undefined) {
			return null;
		}

		const parsedSurgeonId = Number(rawSurgeonId);

		return Number.isFinite(parsedSurgeonId) ? parsedSurgeonId : null;
	}, [session?.surgeon_id, session?.surgeon?.id]);

	const surgeonBillingRules = useMemo(() => {
		if (billingSurgeonId === null) return [];

		const rules = surgeonBillingRulesBySurgeonId[billingSurgeonId];

		return Array.isArray(rules) ? rules : [];
	}, [billingSurgeonId, surgeonBillingRulesBySurgeonId]);

	const [billingRulesLoading, setBillingRulesLoading] = useState(false);
	const [billingRulesLoadedForSurgeonId, setBillingRulesLoadedForSurgeonId] =
		useState<number | null>(null);
	const [billingRulesLoadError, setBillingRulesLoadError] = useState<
		string | null
	>(null);

	useEffect(() => {
		if (billingSurgeonId === null) {
			setBillingRulesLoading(false);
			setBillingRulesLoadedForSurgeonId(null);
			setBillingRulesLoadError(null);
			return;
		}

		let cancelled = false;

		const extractRules = (payload: any) => {
			const candidates = [
				payload,
				payload?.rules,
				payload?.surgeonBillingRules,
				payload?.surgeon_billing_rules,
				payload?.billingRules,
				payload?.data,
				payload?.data?.rules,
				payload?.data?.surgeonBillingRules,
				payload?.data?.surgeon_billing_rules,
				payload?.data?.billingRules,
			];

			const rules = candidates.find((candidate) =>
				Array.isArray(candidate),
			);

			return Array.isArray(rules) ? rules : [];
		};

		const loadBillingRules = async () => {
			setBillingRulesLoading(true);
			setBillingRulesLoadError(null);
			setBillingRulesLoadedForSurgeonId(null);

			try {
				console.log(
					"[OOP] SessionView loading surgeon billing rules:",
					{
						surgeonId: billingSurgeonId,
					},
				);

				const response = await globalAxios.get(
					`${SERVER_URL}/surgeon-billing-rules/${billingSurgeonId}`,
				);

				if (cancelled) return;

				console.log(
					"[OOP] SessionView billing rules raw response:",
					response.data,
				);

				const rules = extractRules(response.data);

				console.log("[OOP] SessionView surgeon billing rules loaded:", {
					surgeonId: billingSurgeonId,
					count: rules.length,
					rules,
				});

				dispatch(
					setSurgeonBillingRulesForSurgeon({
						surgeonId: billingSurgeonId,
						rules,
					}),
				);

				setBillingRulesLoadedForSurgeonId(billingSurgeonId);
			} catch (error: any) {
				if (cancelled) return;

				console.error(
					"[OOP] SessionView failed to load surgeon billing rules:",
					error,
				);

				setBillingRulesLoadError(
					error?.response?.data?.message ||
						error?.message ||
						"Failed to load surgeon billing rules.",
				);
			} finally {
				if (!cancelled) {
					setBillingRulesLoading(false);
				}
			}
		};

		loadBillingRules();

		return () => {
			cancelled = true;
		};
	}, [billingSurgeonId, dispatch]);

	const billingRulesReady =
		billingSurgeonId !== null &&
		billingRulesLoadedForSurgeonId === billingSurgeonId;

	const isEditing = useAppSelector((state) => state.session.isEditing);
	const currentLens = useAppSelector((state) => state.calendar.lens);

	const [hospitals, setHospitals] = useState([]);
	const [surgeons, setSurgeons] = useState([]);

	const [loadingHospitals, setLoadingHospitals] = useState(false);
	const [loadingSurgeons, setLoadingSurgeons] = useState(false);

	const [anchorMenu, setAnchorMenu] = useState(null);
	const openMenu = Boolean(anchorMenu);

	const [savingSession, setSavingSession] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);

	const [deletingSession, setDeletingSession] = useState(false);

	const [sessionItemSets, setSessionItemSets] = useState(() => {
		return (session?.events ?? []).reduce((acc, event) => {
			const raw = event.actual_items;
			let parsed;

			if (Array.isArray(raw)) {
				parsed = raw;
			} else {
				try {
					parsed = JSON.parse(raw);
					if (!Array.isArray(parsed)) parsed = [];
				} catch {
					parsed = [];
				}
			}

			acc[event.id] = parsed;
			return acc;
		}, {});
	});

	useEffect(() => {
		if (!session?.events) return;

		const updatedSets = session.events.reduce((acc, event) => {
			const raw = event.actual_items;
			let parsed;

			if (Array.isArray(raw)) {
				parsed = raw;
			} else {
				try {
					parsed = JSON.parse(raw);
					if (!Array.isArray(parsed)) parsed = [];
				} catch {
					parsed = [];
				}
			}

			acc[event.id] = parsed;
			return acc;
		}, {});

		setSessionItemSets(updatedSets);
	}, [session?.events]);

	const originalItemsByEvent = useMemo(() => {
		if (!session?.events) return {};

		return Object.fromEntries(
			session.events.map((event) => [
				event.id,
				JSON.stringify(event.actual_items || []),
			]),
		);
	}, [session]);

	const hasItemChanges = (eventId) => {
		const originalRaw = originalItemsByEvent[eventId];
		const originalParsed =
			JSON.parse(originalRaw) === "null" ? "[]" : JSON.parse(originalRaw);

		return (
			JSON.stringify(sessionItemSets[eventId.toString()] || []) !==
			originalParsed
		);
	};

	const [newItems, setNewItems] = useState({});
	const [savingStates, setSavingStates] = useState({});
	const [savingIfcByEvent, setSavingIfcByEvent] = useState<
		Record<string, boolean>
	>({});

	useEffect(() => {
		if (session?.events?.length) {
			const itemNumbers = extractItemNumbersFromRedux([session]);
			dispatch(fetchSchedulesIfNeeded(itemNumbers));
		}
		// eslint-disable-next-line
	}, [session?.events]);

	useEffect(() => {
		if (isEditing && (hospitals.length === 0 || surgeons.length === 0)) {
			setLoadingHospitals(true);
			setLoadingSurgeons(true);

			Promise.all([
				globalAxios.get(`${SERVER_URL}/hospitals`),
				globalAxios.get(`${SERVER_URL}/surgeons`),
			])
				.then(([hospitalRes, surgeonRes]) => {
					setHospitals(hospitalRes.data.hospitals);
					setSurgeons(surgeonRes.data.surgeons);
				})
				.catch((error) => {
					console.error("Error fetching dropdown data:", error);
				})
				.finally(() => {
					setLoadingHospitals(false);
					setLoadingSurgeons(false);
				});
		}
		// eslint-disable-next-line
	}, [isEditing]);

	const [oopByEvent, setOopByEvent] = useState<Record<string, string>>({});
	const [oopTouchedByEvent, setOopTouchedByEvent] = useState<
		Record<string, boolean>
	>({});
	const [oopSourceByEvent, setOopSourceByEvent] = useState<
		Record<string, string>
	>({});
	const [oopBaselineByEvent, setOopBaselineByEvent] = useState<
		Record<string, string>
	>({});
	const [clearOopOnSaveByEvent, setClearOopOnSaveByEvent] = useState<
		Record<string, boolean>
	>({});

	const hasServerOopOverride = useCallback((ev: any) => {
		const value = ev?.proposed_oop;

		return (
			value !== undefined &&
			value !== null &&
			String(value).trim() !== "" &&
			Number.isFinite(Number(value))
		);
	}, []);

	const resolveDefaultOopForEvent = useCallback(
		(ev: any) => {
			if (billingSurgeonId === null) {
				return {
					rule: null,
					value: null,
					source: "No surgeon billing rule",
				};
			}

			if (!billingRulesReady) {
				return {
					rule: null,
					value: null,
					source: billingRulesLoading
						? "Loading surgeon billing rule..."
						: billingRulesLoadError
							? "Unable to load surgeon billing rule"
							: "Loading surgeon billing rule...",
				};
			}

			const rule = resolveSurgeonBillingRule(
				ev,
				surgeonBillingRules,
				session,
			);

			if (!rule) {
				return {
					rule: null,
					value: null,
					source: "No OOP",
				};
			}

			const value = Number(rule.default_oop);

			if (!Number.isFinite(value)) {
				return {
					rule,
					value: null,
					source: "No OOP",
				};
			}

			return {
				rule,
				value,
				source: "Surgeon billing rule",
			};
		},
		[
			billingSurgeonId,
			billingRulesReady,
			billingRulesLoading,
			billingRulesLoadError,
			surgeonBillingRules,
			session,
		],
	);

	const appliedSessionBillingRule = useMemo(() => {
		if (!billingRulesReady || !session) {
			return null;
		}

		/*
		 * Normal sessions resolve the billing rule from the first event.
		 * Derived regular sessions do not necessarily have patient events yet,
		 * so build an event-like billing context from the session itself.
		 */
		const firstEvent = session.events?.[0];

		if (firstEvent) {
			return resolveDefaultOopForEvent(firstEvent).rule;
		}

		let dateOfService: string | null = null;

		if (session.start) {
			const startDate = new Date(session.start);

			if (!Number.isNaN(startDate.getTime())) {
				dateOfService = startDate.toLocaleDateString("en-CA");
			}
		}

		const regularSessionBillingContext = {
			date_of_service: dateOfService,
			hospital_id: session.hospital_id ?? session.hospital?.id ?? null,
			hospital: session.hospital ?? null,
		};

		return resolveDefaultOopForEvent(regularSessionBillingContext).rule;
	}, [billingRulesReady, session, resolveDefaultOopForEvent]);

	useEffect(() => {
		if (!session?.events) return;

		const nextOop: Record<string, string> = {};
		const nextTouched: Record<string, boolean> = {};
		const nextSource: Record<string, string> = {};
		const nextBaseline: Record<string, string> = {};
		const nextClear: Record<string, boolean> = {};

		session.events.forEach((ev) => {
			if (hasServerOopOverride(ev)) {
				const value = Number(ev.proposed_oop).toFixed(2);

				nextOop[ev.id] = value;
				nextBaseline[ev.id] = value;
				nextTouched[ev.id] = false;
				nextSource[ev.id] = "Manual OOP";
				nextClear[ev.id] = false;
				return;
			}

			if (billingSurgeonId === null) {
				nextOop[ev.id] = "0.00";
				nextBaseline[ev.id] = "0.00";
				nextTouched[ev.id] = false;
				nextSource[ev.id] = "No surgeon billing rule";
				nextClear[ev.id] = false;
				return;
			}

			if (!billingRulesReady) {
				nextOop[ev.id] = "";
				nextBaseline[ev.id] = "";
				nextTouched[ev.id] = false;
				nextSource[ev.id] = billingRulesLoading
					? "Loading surgeon billing rule..."
					: billingRulesLoadError
						? "Unable to load surgeon billing rule"
						: "Loading surgeon billing rule...";
				nextClear[ev.id] = false;
				return;
			}

			const { rule, value, source } = resolveDefaultOopForEvent(ev);
			const displayValue = value !== null ? value.toFixed(2) : "0.00";

			console.log("[OOP] SessionView resolved event default:", {
				eventId: ev.id,
				date: ev.date_of_service,
				hospitalId:
					ev.hospital_id ??
					ev.hospital?.id ??
					session?.hospital_id ??
					session?.hospital?.id ??
					null,
				ruleId: rule?.id ?? null,
				default_oop: rule?.default_oop ?? null,
				value,
			});

			nextOop[ev.id] = displayValue;
			nextBaseline[ev.id] = displayValue;
			nextTouched[ev.id] = false;
			nextSource[ev.id] = source;
			nextClear[ev.id] = false;
		});

		setOopByEvent(nextOop);
		setOopTouchedByEvent(nextTouched);
		setOopSourceByEvent(nextSource);
		setOopBaselineByEvent(nextBaseline);
		setClearOopOnSaveByEvent(nextClear);
	}, [
		session?.events,
		session?.hospital_id,
		session?.hospital?.id,
		billingSurgeonId,
		billingRulesReady,
		billingRulesLoading,
		billingRulesLoadError,
		resolveDefaultOopForEvent,
		hasServerOopOverride,
	]);

	const handleOopChange = (eventId: string, val: string) => {
		if (!/^\d*\.?\d*$/.test(val)) return;

		setOopByEvent((prev) => ({
			...prev,
			[eventId]: val,
		}));

		setOopTouchedByEvent((prev) => ({
			...prev,
			[eventId]: !equalMoney(val, oopBaselineByEvent[eventId] ?? ""),
		}));

		setClearOopOnSaveByEvent((prev) => ({
			...prev,
			[eventId]: false,
		}));
	};

	const handleOopBlur = (eventId: string) => {
		const ev = session?.events?.find(
			(event) => String(event.id) === String(eventId),
		);

		if (!ev) return;

		const currentValue = oopByEvent[eventId] ?? "";
		const norm = normaliseCurrency(currentValue);

		if (norm !== "") {
			setOopByEvent((prev) => ({
				...prev,
				[eventId]: norm,
			}));

			setOopTouchedByEvent((prev) => ({
				...prev,
				[eventId]: !equalMoney(norm, oopBaselineByEvent[eventId] ?? ""),
			}));

			return;
		}

		const { rule, value, source } = resolveDefaultOopForEvent(ev);
		const defaultValue = rule && value !== null ? value.toFixed(2) : "0.00";

		setOopByEvent((prev) => ({
			...prev,
			[eventId]: defaultValue,
		}));

		setOopSourceByEvent((prev) => ({
			...prev,
			[eventId]: source,
		}));

		setOopBaselineByEvent((prev) => ({
			...prev,
			[eventId]: defaultValue,
		}));

		setOopTouchedByEvent((prev) => ({
			...prev,
			[eventId]: false,
		}));

		setClearOopOnSaveByEvent((prev) => ({
			...prev,
			[eventId]: hasServerOopOverride(ev),
		}));
	};

	const handleApplyDefaultOOP = (ev: any) => {
		if (billingSurgeonId === null) {
			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(
				setSnackbarText(
					"Cannot apply default OOP because no surgeon is available.",
				),
			);
			return;
		}

		if (!billingRulesReady) {
			console.warn(
				"[OOP] SessionView Apply default blocked: billing rules not loaded",
				{
					eventId: ev?.id,
					surgeonId: billingSurgeonId,
					loading: billingRulesLoading,
					error: billingRulesLoadError,
				},
			);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(
				setSnackbarText(
					billingRulesLoadError ||
						"Surgeon billing rules are still loading.",
				),
			);
			return;
		}

		const { rule, value } = resolveDefaultOopForEvent(ev);

		console.log("[OOP] SessionView Apply default:", {
			eventId: ev.id,
			proposed_oop: ev.proposed_oop,
			surgeonId: billingSurgeonId,
			date: ev.date_of_service,
			hospitalId:
				ev.hospital_id ??
				ev.hospital?.id ??
				session?.hospital_id ??
				session?.hospital?.id ??
				null,
			ruleCount: surgeonBillingRules.length,
			ruleId: rule?.id ?? null,
			default_oop: rule?.default_oop ?? null,
			resolvedValue: value,
		});

		if (!rule || value === null) {
			console.warn(
				"[OOP] SessionView Apply default: no matching billing rule",
				{
					eventId: ev.id,
					rules: surgeonBillingRules,
				},
			);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(
				setSnackbarText("No matching surgeon billing rule was found."),
			);
			return;
		}

		const val = value.toFixed(2);
		const hasServerOverride = hasServerOopOverride(ev);

		setOopByEvent((prev) => ({
			...prev,
			[ev.id]: val,
		}));

		setOopSourceByEvent((prev) => ({
			...prev,
			[ev.id]: "Surgeon billing rule",
		}));

		setOopTouchedByEvent((prev) => ({
			...prev,
			[ev.id]: false,
		}));

		setOopBaselineByEvent((prev) => ({
			...prev,
			[ev.id]: val,
		}));

		setClearOopOnSaveByEvent((prev) => ({
			...prev,
			[ev.id]: hasServerOverride,
		}));
	};

	const hasOopChanges = (ev) => {
		if (clearOopOnSaveByEvent[ev.id]) return true;

		const touched = !!oopTouchedByEvent[ev.id];
		if (!touched) return false;

		const uiNorm = normaliseCurrency(oopByEvent[ev.id] ?? "");

		const serverNorm =
			ev.proposed_oop == null || ev.proposed_oop === ""
				? ""
				: Number(ev.proposed_oop).toFixed(2);

		return uiNorm !== serverNorm;
	};

	const sessionEventsForCalc = useMemo(() => {
		if (!session?.events) return [];

		return session.events.map((ev) => {
			const raw = oopByEvent[ev.id];
			const parsed = Number(raw);

			const hasResolvedOop =
				raw !== undefined &&
				raw !== null &&
				String(raw).trim() !== "" &&
				Number.isFinite(parsed);

			return {
				...ev,
				proposed_oop: hasResolvedOop ? parsed : 0,
			};
		});
	}, [session?.events, oopByEvent]);

	const changesMade = useMemo(() => {
		return !_.isEqual(session, originalSession);
	}, [session, originalSession]);

	if (!session) return null;

	const handleBack = () => {
		dispatch(clearSelectedSession());
	};

	const handleMenuClick = (event) => {
		setAnchorMenu(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorMenu(null);
	};

	const handleUpdateSessionTime = (field, value) => {
		if (!value) return;

		const updatedTime = new Date(value);
		updatedTime.setSeconds(0);
		updatedTime.setMilliseconds(0);

		const existingStart = new Date(session.start);
		const existingEnd = new Date(session.end);

		if (field === "end") {
			if (
				updatedTime.getHours() === 0 &&
				updatedTime.getMinutes() === 0
			) {
				updatedTime.setMinutes(1);
			}

			if (updatedTime <= existingStart) {
				const adjustedStart = new Date(updatedTime);
				adjustedStart.setMinutes(adjustedStart.getMinutes() - 1);

				dispatch(
					updateSessionField({
						field: "start",
						value: adjustedStart,
					}),
				);

				dispatch(
					updateSessionField({
						field: "end",
						value: updatedTime,
					}),
				);

				return;
			}
		}

		if (field === "start") {
			if (updatedTime >= existingEnd) {
				const adjustedEnd = new Date(updatedTime);
				adjustedEnd.setMinutes(adjustedEnd.getMinutes() + 1);

				dispatch(
					updateSessionField({
						field: "start",
						value: updatedTime,
					}),
				);

				dispatch(
					updateSessionField({
						field: "end",
						value: adjustedEnd,
					}),
				);

				return;
			}
		}

		dispatch(
			updateSessionField({
				field,
				value: updatedTime,
			}),
		);
	};

	const handleSaveSession = async () => {
		if (
			!session?.start ||
			!session?.end ||
			!session?.hospital ||
			!session?.surgeon
		) {
			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(setSnackbarText("Please fill all required fields."));
			return;
		}

		try {
			setSaveSuccess(false);
			setSavingSession(true);

			const updatedSessionData = {
				date_of_service: new Date(session.start).toLocaleDateString(
					"en-CA",
				),
				start_time: new Date(session.start).toLocaleTimeString(
					"en-AU",
					{
						hour: "2-digit",
						minute: "2-digit",
						hour12: false,
					},
				),
				end_time: new Date(session.end).toLocaleTimeString("en-AU", {
					hour: "2-digit",
					minute: "2-digit",
					hour12: false,
				}),
				hospital: session.hospital,
				surgeon: session.surgeon,
			};

			const response = await globalAxios.patch(
				`${SERVER_URL}/user/sessions/${session.id}`,
				updatedSessionData,
			);

			if (response.status === 200 && response.data.session) {
				playSuccessSave();

				dispatch(
					updateSession({
						sessionId: session.id,
						updates: response.data.session,
					}),
				);

				dispatch(saveSessionEdits());
				dispatch(setSessionEditMode(false));

				dispatch(setSnackbarOpen(true));
				dispatch(setSnackbarSeverity("success"));
				dispatch(setSnackbarText("Session updated successfully."));

				setSaveSuccess(true);
				setTimeout(() => setSaveSuccess(false), 2500);
			} else {
				throw new Error(
					response.data.message || "Failed to update session.",
				);
			}
		} catch (error) {
			console.error("Error updating session:", error);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(setSnackbarText("Error updating session."));
		} finally {
			setSavingSession(false);
		}
	};

	const handleDelete = async () => {
		if (!session?.id) {
			console.error("No session selected or missing session ID.");
			return;
		}

		setDeletingSession(true);

		try {
			const response = await globalAxios.delete(
				`${SERVER_URL}/user/sessions/${session.id}`,
			);

			if (response.status === 200) {
				console.log("Session deleted successfully.");

				playDelete();

				dispatch(removeSession(parseInt(session.id)));
				dispatch(clearSelectedSession());
			}
		} catch (error) {
			console.error("Error deleting session:", error);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(setSnackbarText("Failed to delete session."));
		} finally {
			setDeletingSession(false);
		}
	};

	const handleItemChange = (eventId, index, value) => {
		const updated = [...(sessionItemSets[eventId] || [])];
		const parsed = parseInt(value.trim(), 10);

		if (!isNaN(parsed)) {
			updated[index] = parsed;

			setSessionItemSets({
				...sessionItemSets,
				[eventId]: updated,
			});
		}
	};

	const handleDeleteItem = (eventId, index) => {
		const updated = [...(sessionItemSets[eventId] || [])];

		updated.splice(index, 1);

		setSessionItemSets({
			...sessionItemSets,
			[eventId]: updated,
		});
	};

	const handleAddItem = (eventId) => {
		const trimmed = (newItems[eventId] || "").trim();

		if (!isNaN(Number(trimmed && trimmed !== ""))) {
			const updated = [...(sessionItemSets[eventId] || []), trimmed];

			setSessionItemSets({
				...sessionItemSets,
				[eventId]: updated,
			});

			setNewItems({
				...newItems,
				[eventId]: "",
			});
		}
	};

	const setSavingForEvent = (eventId, isSaving) => {
		setSavingStates((prev) => ({
			...prev,
			[eventId]: isSaving,
		}));
	};

	const handleCancelChanges = (eventId: string) => {
		if (!originalItemsByEvent[eventId]) return;

		setSessionItemSets((prev) => ({
			...prev,
			[eventId]: JSON.parse(JSON.parse(originalItemsByEvent[eventId])),
		}));
	};

	const handleToggleIFCSent = async (ev: any, checked: boolean) => {
		if (!session?.id || !ev?.id || ev.ifc_required !== true) return;

		const eventId = String(ev.id);
		const previousIfcSent = ev.ifc_sent === true;

		const updateSessionWithIfcSent = (ifcSent: boolean) => ({
			...session,
			events: session.events.map((sessionEvent) =>
				String(sessionEvent.id) === eventId
					? { ...sessionEvent, ifc_sent: ifcSent }
					: sessionEvent,
			),
		});

		setSavingIfcByEvent((prev) => ({
			...prev,
			[eventId]: true,
		}));

		// Optimistic update so the Session View and calendar funding colour
		// respond immediately.
		dispatch(setSelectedSession(updateSessionWithIfcSent(checked)));
		dispatch(
			updateEvent({
				sessionId: session.id.toString(),
				eventId: Number(ev.id),
				updates: { ifc_sent: checked },
			}),
		);

		try {
			const response = await globalAxios.patch(
				`${SERVER_URL}/user/events/${ev.id}`,
				{ ifc_sent: checked },
			);

			const responseEvent =
				response.data?.updatedEvent || response.data?.event;

			if (responseEvent) {
				const updatedEvent = { ...ev, ...responseEvent };

				dispatch(
					setSelectedSession({
						...session,
						events: session.events.map((sessionEvent) =>
							String(sessionEvent.id) === eventId
								? { ...sessionEvent, ...updatedEvent }
								: sessionEvent,
						),
					}),
				);

				dispatch(
					updateEvent({
						sessionId: session.id.toString(),
						eventId: Number(ev.id),
						updates: updatedEvent,
					}),
				);
			}
		} catch (error) {
			console.error("[IFC] Failed to update IFC sent:", error);

			dispatch(
				setSelectedSession(updateSessionWithIfcSent(previousIfcSent)),
			);
			dispatch(
				updateEvent({
					sessionId: session.id.toString(),
					eventId: Number(ev.id),
					updates: { ifc_sent: previousIfcSent },
				}),
			);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(setSnackbarText("Failed to update IFC status."));
		} finally {
			setSavingIfcByEvent((prev) => ({
				...prev,
				[eventId]: false,
			}));
		}
	};

	const handleValidateAndSaveItems = async (eventId) => {
		const items = sessionItemSets[eventId] || [];

		const parsedItems = items
			.map((item) => parseInt(item, 10))
			.filter((item) => !isNaN(item))
			.map((num) => num.toString());

		try {
			setSavingForEvent(eventId, true);

			const payload: any = {
				actual_items: parsedItems,
			};

			const uiRaw = oopByEvent[eventId] ?? "";
			const uiNorm = normaliseCurrency(uiRaw);

			if (oopTouchedByEvent[eventId]) {
				const n = Number(uiNorm);

				payload.proposed_oop =
					uiNorm.trim() === "" || !Number.isFinite(n)
						? null
						: n.toFixed(2);
			} else if (clearOopOnSaveByEvent[eventId]) {
				payload.proposed_oop = null;
			}

			const response = await globalAxios.put(
				`${SERVER_URL}/user/events/${eventId}`,
				payload,
			);

			if (response.status === 200) {
				playSuccessSave();

				const { updatedEvent, schedules: returnedSchedules } =
					response.data;

				if (
					Array.isArray(returnedSchedules) &&
					returnedSchedules.length > 0
				) {
					dispatch(upsertSchedules(returnedSchedules));
				}

				dispatch(
					setSelectedSession({
						...session,
						events: session.events.map((ev) =>
							ev.id === updatedEvent.id
								? { ...updatedEvent }
								: ev,
						),
					}),
				);

				dispatch(setSelectedEvent(updatedEvent));

				dispatch(
					updateEvent({
						eventId: updatedEvent.id,
						sessionId: session.id.toString(),
						updates: updatedEvent,
					}),
				);

				let nextVal: string;
				let nextSource: string;

				if (
					typeof updatedEvent.proposed_oop !== "undefined" &&
					updatedEvent.proposed_oop !== null &&
					String(updatedEvent.proposed_oop).trim() !== "" &&
					Number.isFinite(Number(updatedEvent.proposed_oop))
				) {
					nextVal = Number(updatedEvent.proposed_oop).toFixed(2);
					nextSource = "Manual OOP";
				} else {
					const { rule, value, source } =
						resolveDefaultOopForEvent(updatedEvent);

					console.log("[OOP] SessionView post-save default:", {
						eventId: updatedEvent.id,
						ruleId: rule?.id ?? null,
						default_oop: rule?.default_oop ?? null,
						value,
					});

					nextVal = value !== null ? value.toFixed(2) : "0.00";

					nextSource = source;
				}

				setOopByEvent((prev) => ({
					...prev,
					[eventId]: nextVal,
				}));

				setOopSourceByEvent((prev) => ({
					...prev,
					[eventId]: nextSource,
				}));

				setOopBaselineByEvent((prev) => ({
					...prev,
					[eventId]: nextVal,
				}));

				setOopTouchedByEvent((prev) => ({
					...prev,
					[eventId]: false,
				}));

				setClearOopOnSaveByEvent((prev) => ({
					...prev,
					[eventId]: false,
				}));
			} else {
				console.warn("Unexpected response", response);
			}
		} catch (err) {
			console.error("❌ Error saving event changes:", err);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(setSnackbarText("Failed to save changes."));
		} finally {
			setSavingForEvent(eventId, false);
		}
	};

	const handlePatientClick = async (event) => {
		if (!event.patient.id) return;

		const patientId = event.patient.id;

		dispatch(setLoadingDetail(true));
		dispatch(
			setSelectedItem({
				...event.patient,
				type: "patient",
			}),
		);

		navigate("/search");

		try {
			const res = await globalAxios.get(
				`${SERVER_URL}/search/patient/${patientId}`,
			);

			dispatch(
				setSelectedItem({
					...res.data,
					type: "patient",
				}),
			);

			dispatch(setLoadingDetail(false));
		} catch (err) {
			console.error("Failed to fetch patient with linked events", err);
		}
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box sx={{ maxWidth: "1200px", width: "100%", px: 2 }}>
				<Box
					sx={{
						display: "flex",
						gap: 2,
						mb: 4,
						flexWrap: "wrap",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flex: 1.5,
							minWidth: {
								xs: "100%",
								md: "0",
							},
						}}
					>
						<Button
							variant="outlined"
							onClick={handleBack}
							sx={{
								borderRadius: "8px 0 0 8px",
								borderRight: "none",
								backgroundColor: "grey.200",
								color: "text.primary",
								borderColor: "grey.400",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								minWidth: 56,
								px: 1,
								py: 1.5,
								"&:hover": {
									backgroundColor: "grey.300",
									borderColor: "grey.400",
								},
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
							}}
						>
							<ArrowBackIcon />
						</Button>

						<Box
							sx={{
								border: "1px solid #ccc",
								borderLeft: "none",
								borderRadius: "0 8px 8px 0",
								flex: 1,
							}}
						>
							<Box
								sx={{
									borderRadius: "0 7px 0 0",
									backgroundColor: (theme) =>
										theme.palette.primary.main,
									color: (theme) =>
										theme.palette.primary.contrastText,
									px: 2,
									pt: 0.2,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<Typography variant="h6" fontWeight="bold">
									List Details
								</Typography>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									{deletingSession ? (
										<Box
											sx={{
												position: "relative",
												width: 20,
												height: 20,
												display: "inline-block",
											}}
										>
											<DeleteOutlineIcon
												color="error"
												fontSize="small"
												sx={{
													position: "absolute",
													animation:
														"binFlicker 800ms infinite",
													opacity: 1,
												}}
											/>

											<DeleteIcon
												color="error"
												fontSize="small"
												sx={{
													position: "absolute",
													animation:
														"binFlickerAlt 800ms infinite",
													opacity: 0,
												}}
											/>
										</Box>
									) : savingSession ? (
										<SaveIcon
											color="warning"
											fontSize="small"
											sx={{
												animation:
													"pulseInward 1s infinite",
											}}
										/>
									) : saveSuccess ? (
										<SaveIcon
											color="success"
											fontSize="small"
											sx={{
												animation:
													"fadeOutSuccess 1.5s forwards",
											}}
										/>
									) : changesMade ? (
										<Tooltip title="Unsaved changes">
											<WarningAmberIcon
												color="warning"
												fontSize="small"
											/>
										</Tooltip>
									) : null}

									<IconButton
										size="small"
										disableRipple
										onClick={handleMenuClick}
										sx={{
											color: "inherit",
											padding: 0,
										}}
									>
										<SettingsIcon />
									</IconButton>
								</Box>

								<Menu
									elevation={2}
									anchorEl={anchorMenu}
									open={openMenu}
									onClose={handleMenuClose}
									transformOrigin={{
										horizontal: "right",
										vertical: "top",
									}}
									anchorOrigin={{
										horizontal: "right",
										vertical: "bottom",
									}}
									sx={{
										marginTop: "4px",
									}}
								>
									{!isEditing ? (
										<MenuItem
											onClick={() => {
												dispatch(
													setSessionEditMode(true),
												);

												handleMenuClose();
											}}
										>
											Edit
										</MenuItem>
									) : (
										<>
											<MenuItem
												onClick={() => {
													handleSaveSession();

													dispatch(
														saveSessionEdits(),
													);

													handleMenuClose();
												}}
												disabled={!changesMade}
											>
												Save
											</MenuItem>

											<MenuItem
												onClick={() => {
													dispatch(
														resetSessionEdits(),
													);

													handleMenuClose();
												}}
											>
												Cancel
											</MenuItem>
										</>
									)}

									<MenuItem
										onClick={() => {
											const confirmed = window.confirm(
												"Are you sure you want to delete this session? This action cannot be undone.",
											);

											if (confirmed) {
												handleDelete();
												handleMenuClose();
											}
										}}
										sx={{
											color: "error.main",
										}}
									>
										Delete
									</MenuItem>
								</Menu>
							</Box>

							<Box sx={{ px: 2, py: 1 }}>
								{isEditing ? (
									<DatePicker
										label="Date"
										format="dd/MM/yyyy"
										slotProps={{
											textField: {
												size: "small",
											},
										}}
										value={new Date(session.start)}
										onChange={(newDate) => {
											if (!newDate) return;

											const startTime = new Date(
												session.start,
											);

											const endTime = new Date(
												session.end,
											);

											const newStart = new Date(newDate);

											newStart.setHours(
												startTime.getHours(),
												startTime.getMinutes(),
												0,
												0,
											);

											const newEnd = new Date(newDate);

											newEnd.setHours(
												endTime.getHours(),
												endTime.getMinutes(),
												0,
												0,
											);

											dispatch(
												updateSessionField({
													field: "start",
													value: newStart,
												}),
											);

											dispatch(
												updateSessionField({
													field: "end",
													value: newEnd,
												}),
											);
										}}
									/>
								) : (
									<Typography variant="body1" gutterBottom>
										<strong>Date:</strong>{" "}
										{new Date(
											session.start,
										).toLocaleDateString("en-AU")}
									</Typography>
								)}

								{isEditing ? (
									<Autocomplete
										size="small"
										disableListWrap
										loading={loadingSurgeons}
										options={surgeons}
										getOptionLabel={(option) =>
											option.first_name +
											" " +
											option.last_name
										}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										value={session?.surgeon || null}
										onChange={(_, newValue) =>
											dispatch(
												updateSessionField({
													field: "surgeon",
													value: newValue,
												}),
											)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												sx={{
													minWidth: 100,
												}}
											/>
										)}
										renderOption={(props, option) => (
											<li {...props} key={option.id}>
												{option.first_name +
													" " +
													option.last_name}
											</li>
										)}
										ListboxComponent={ListboxComponent}
									/>
								) : (
									<Typography variant="body1" gutterBottom>
										<strong>Surgeon:</strong>{" "}
										{session.surgeon?.name ||
											(session.surgeon?.first_name
												? `${session.surgeon.first_name} ${session.surgeon.last_name}`
												: "—")}
									</Typography>
								)}

								{isEditing ? (
									<Autocomplete
										size="small"
										disableListWrap
										loading={loadingHospitals}
										options={hospitals}
										getOptionLabel={(option) => option.name}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										value={session?.hospital || null}
										onChange={(_, newValue) =>
											dispatch(
												updateSessionField({
													field: "hospital",
													value: newValue,
												}),
											)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												sx={{
													minWidth: 100,
												}}
											/>
										)}
										renderOption={(props, option) => (
											<li {...props} key={option.id}>
												{option.name}
											</li>
										)}
										ListboxComponent={ListboxComponent}
									/>
								) : (
									<Typography variant="body1" gutterBottom>
										<strong>Hospital:</strong>{" "}
										{session.hospital?.name || "—"}
									</Typography>
								)}

								{isEditing ? (
									<TimePicker
										value={
											session?.start
												? new Date(session.start)
												: null
										}
										onChange={(newValue) =>
											handleUpdateSessionTime(
												"start",
												newValue,
											)
										}
										ampm={false}
										slotProps={{
											textField: {
												size: "small",
											},
										}}
									/>
								) : (
									<Typography variant="body1" gutterBottom>
										<strong>From:</strong>{" "}
										{new Date(
											session.start,
										).toLocaleTimeString("en-AU", {
											hour: "2-digit",
											minute: "2-digit",
											hour12: false,
										})}
									</Typography>
								)}

								{isEditing ? (
									<TimePicker
										value={
											session?.end
												? toDate(session.end)
												: session?.start
													? toDate(
															calculateEndTime(
																session.start
																	.toISOString()
																	.split(
																		"T",
																	)[0],
																session.start
																	.toTimeString()
																	.slice(
																		0,
																		5,
																	),
																session.nextEvent ??
																	null,
															),
														)
													: null
										}
										onChange={(newValue) =>
											handleUpdateSessionTime(
												"end",
												newValue,
											)
										}
										ampm={false}
										slotProps={{
											textField: {
												size: "small",
											},
										}}
									/>
								) : (
									<Typography variant="body1" gutterBottom>
										<strong>To:</strong>{" "}
										{new Date(
											session.end,
										).toLocaleTimeString("en-AU", {
											hour: "2-digit",
											minute: "2-digit",
											hour12: false,
										})}
									</Typography>
								)}

								<Typography variant="body1">
									<strong>Assistant Estimate:</strong> $
									{calculateFeeEstimate(
										userSettings,
										sessionEventsForCalc,
										schedules,
									)?.toFixed(2) ?? "0.00"}
								</Typography>
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							flex: 1,
							minWidth: {
								xs: "100%",
								md: "0",
							},
							border: "1px solid #aaa",
							borderRadius: 2,
							px: 2,
							py: 2,
							backgroundColor: "#f9f9f9",
						}}
					>
						<Typography
							variant="subtitle1"
							fontWeight="bold"
							sx={{ mb: 1 }}
						>
							Surgeon Billing Rule
						</Typography>

						{billingRulesLoading ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								<CircularProgress size={16} />

								<Typography
									variant="body2"
									color="text.secondary"
								>
									Loading billing rule...
								</Typography>
							</Box>
						) : billingRulesLoadError ? (
							<Typography variant="body2" color="error.main">
								Unable to load billing rule
							</Typography>
						) : appliedSessionBillingRule ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexWrap: "wrap",
										gap: 1,
									}}
								>
									<Chip
										size="small"
										label={
											appliedSessionBillingRule.default_oop !==
												null &&
											appliedSessionBillingRule.default_oop !==
												undefined &&
											String(
												appliedSessionBillingRule.default_oop,
											).trim() !== "" &&
											Number.isFinite(
												Number(
													appliedSessionBillingRule.default_oop,
												),
											)
												? `$${Number(
														appliedSessionBillingRule.default_oop,
													).toFixed(2)} OOP`
												: "No OOP"
										}
									/>

									<Chip
										size="small"
										label={
											appliedSessionBillingRule.ifc_required
												? "IFC Required"
												: "IFC Not Required"
										}
									/>
								</Box>

								{appliedSessionBillingRule.location && (
									<Typography variant="body2">
										<strong>Location:</strong>{" "}
										{appliedSessionBillingRule.location}
									</Typography>
								)}

								{appliedSessionBillingRule.start_date && (
									<Typography
										variant="body2"
										color="text.secondary"
									>
										<strong>Effective:</strong>{" "}
										{new Date(
											`${appliedSessionBillingRule.start_date}T00:00:00`,
										).toLocaleDateString("en-AU")}
										{" – "}
										{appliedSessionBillingRule.end_date
											? new Date(
													`${appliedSessionBillingRule.end_date}T00:00:00`,
												).toLocaleDateString("en-AU")
											: "Ongoing"}
									</Typography>
								)}
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary">
								No surgeon billing rule applied
							</Typography>
						)}
					</Box>
				</Box>

				<Box
					sx={{
						border: "1px solid #ccc",
						mb: 2,
					}}
				>
					<Box
						sx={{
							backgroundColor: (theme) =>
								theme.palette.primary.main,
							color: (theme) =>
								theme.palette.primary.contrastText,
							px: 2,
							pt: 0.2,
						}}
					>
						<Typography variant="h6" fontWeight="bold">
							Cases
						</Typography>
					</Box>

					{Array.isArray(session?.events) &&
						session.events.map((event, eventIndex) => {
							const fundingColour = setEventColor(
								userSettings,
								"fundingStatus",
								{
									events: [event],
								},
								schedules,
							);

							return (
								<Box
									key={event.id}
									sx={{
										display: "flex",
										alignItems: "stretch",
									}}
								>
									<Accordion
										key={event.id}
										sx={{
											flex: 1,
											backgroundColor:
												currentLens === "fundingStatus"
													? `${fundingColour}15`
													: "#fafafa",
											borderLeft:
												currentLens === "fundingStatus"
													? `6px solid ${fundingColour}`
													: "none",
											boxShadow: "none",
											margin: 0,
											borderBottom: "1px solid #ccc",
											borderRadius: "0!important",
											"&:before": {
												display: "none",
											},
											"&.Mui-expanded": {
												margin: 0,
											},
										}}
									>
										<AccordionSummary
											expandIcon={<ExpandMoreIcon />}
											aria-controls={`event-content-${event.id}`}
											id={`event-header-${event.id}`}
											sx={{
												minHeight: 48,
												"&.Mui-expanded": {
													minHeight: 48,
												},
												"& .MuiAccordionSummary-content":
													{
														margin: 0,
														alignItems: "center",
													},
												"& .MuiAccordionSummary-content.Mui-expanded":
													{
														margin: 0,
													},
											}}
										>
											<PersonIcon fontSize="small" />

											<Box
												sx={{
													ml: 1,
													flex: 1,
													display: "flex",
													alignItems: "center",
													flexWrap: "wrap",
													columnGap: 1,
													rowGap: 0.5,
												}}
											>
												<Typography
													sx={{
														cursor: "pointer",
														color: "primary.main",
													}}
													onClick={() =>
														handlePatientClick(
															event,
														)
													}
												>
													{event.patient?.first_name}{" "}
													{event.patient?.last_name ||
														"—"}
												</Typography>

												{(() => {
													let items = [];

													try {
														items =
															!event.actual_items ||
															event.actual_items ===
																"null"
																? []
																: JSON.parse(
																		event.actual_items,
																	);
													} catch {
														items = [];
													}

													return items.length ===
														0 ? (
														<Typography
															variant="caption"
															color="error"
														>
															No items
														</Typography>
													) : null;
												})()}

												{(hasItemChanges(event.id) ||
													hasOopChanges(event)) && (
													<Typography
														variant="caption"
														color="warning.main"
													>
														Unsaved changes
													</Typography>
												)}
											</Box>

											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													mr: 2,
												}}
											>
												{event?.items_validated ===
													true && (
													<Tooltip
														placement="left"
														title="All items valid"
													>
														<CheckCircleIcon
															sx={{
																color: "green",
															}}
															fontSize="small"
														/>
													</Tooltip>
												)}

												{event?.items_validated ===
													false && (
													<Tooltip title="Some items may be invalid">
														<WarningAmberIcon
															sx={{
																color: "orange",
															}}
															fontSize="small"
														/>
													</Tooltip>
												)}

												<Typography
													variant="body2"
													color="text.secondary"
												>
													{event.procedure || "—"}
												</Typography>

												{event?.specialty ? (
													<Chip
														label={event.specialty}
														size="small"
														variant="outlined"
														sx={{
															ml: 0.5,
														}}
													/>
												) : null}
											</Box>
										</AccordionSummary>

										<AccordionDetails>
											<Grid container spacing={2}>
												<Grid item xs={12} md={3}>
													<Box
														sx={{
															px: 2,
															py: 1,
															bgcolor: "#f0f0f0",
															borderRadius: 1,
															height: "100%",
														}}
													>
														<Typography variant="body2">
															<strong>
																DOB:
															</strong>{" "}
															{event.patient
																?.date_of_birth ||
																"—"}
														</Typography>

														<Typography variant="body2">
															<strong>
																Address:
															</strong>{" "}
															{event.patient
																?.address ||
																"—"}
														</Typography>

														<Typography variant="body2">
															<strong>
																Suburb:
															</strong>{" "}
															{event.patient
																?.suburb || "—"}
														</Typography>

														<Typography variant="body2">
															<strong>
																Postcode:
															</strong>{" "}
															{event.patient
																?.postcode ||
																"—"}
														</Typography>

														<Typography variant="body2">
															<strong>
																Medicare:
															</strong>{" "}
															{event.patient
																?.medicare ||
																"—"}
														</Typography>
													</Box>
												</Grid>

												<Grid item xs={12} md={4}>
													<Box
														sx={{
															px: 1,
														}}
													>
														<Typography
															variant="body2"
															sx={{
																mb: 1,
															}}
														>
															<strong>
																Health Fund:
															</strong>{" "}
															{event.patient
																?.policies?.[0]
																?.healthFund
																?.name ||
																event.patient
																	?.policies?.[0]
																	?.healthFund
																	?.aliases?.[1] ||
																"—"}
														</Typography>

														<Typography
															variant="body2"
															sx={{
																mb: 1,
															}}
														>
															<strong>
																Fund No.:
															</strong>{" "}
															{event.patient
																?.policies?.[0]
																?.health_fund_no ||
																"—"}
														</Typography>

														<Typography
															variant="body2"
															sx={{
																mb: 1,
															}}
														>
															<strong>
																Anaesthetist:
															</strong>{" "}
															{event.anaesthetist
																?.name || "—"}
														</Typography>

														<Typography
															variant="body2"
															sx={{
																mb: 1,
															}}
														>
															<strong>
																Assistant
																Estimate:
															</strong>{" "}
															{(() => {
																const raw =
																	oopByEvent[
																		event.id
																	];

																const parsed =
																	Number(raw);

																const hasResolvedOop =
																	raw !==
																		undefined &&
																	raw !==
																		null &&
																	String(
																		raw,
																	).trim() !==
																		"" &&
																	Number.isFinite(
																		parsed,
																	);

																const evForCalc =
																	{
																		...event,
																		proposed_oop:
																			hasResolvedOop
																				? parsed
																				: 0,
																	};

																const amt =
																	calculateSinglePtFeeEstimate(
																		userSettings,
																		evForCalc,
																		schedules,
																	) ?? 0;

																return `$${amt.toFixed(
																	2,
																)}`;
															})()}
														</Typography>

														<Box
															sx={{
																display: "flex",
																alignItems:
																	"center",
																gap: 1,
																mb: 1,
															}}
														>
															<TextField
																value={
																	oopByEvent[
																		event.id
																	] ?? ""
																}
																onChange={(e) =>
																	handleOopChange(
																		event.id,
																		e.target
																			.value,
																	)
																}
																onBlur={() =>
																	handleOopBlur(
																		event.id,
																	)
																}
																placeholder="0.00"
																size="small"
																sx={{
																	maxWidth: 160,
																	bgcolor:
																		"background.paper",
																}}
																inputProps={{
																	inputMode:
																		"decimal",
																}}
																InputProps={{
																	startAdornment:
																		(
																			<span
																				style={{
																					marginRight: 6,
																				}}
																			>
																				$
																			</span>
																		),
																}}
															/>

															<Button
																variant="text"
																size="small"
																onClick={() =>
																	handleApplyDefaultOOP(
																		event,
																	)
																}
																disabled={
																	!billingRulesReady ||
																	billingRulesLoading
																}
																sx={{
																	textTransform:
																		"none",
																	px: 0.5,
																}}
															>
																Apply default
															</Button>
														</Box>

														<Typography
															variant="caption"
															color="text.secondary"
															sx={{
																display:
																	"block",
																mb: 1,
																whiteSpace:
																	"pre-line",
															}}
														>
															{oopTouchedByEvent[
																event.id
															] ? (
																<>
																	{
																		"OOP:\nIncludes overridden OOP."
																	}
																</>
															) : (
																<>
																	<b>OOP:</b>
																	{"\n"}
																	{oopSourceByEvent[
																		event.id
																	] || "—"}
																</>
															)}
														</Typography>

														{event.ifc_required ===
															true && (
															<Box
																sx={{
																	display:
																		"flex",
																	alignItems:
																		"center",
																	mb: 1,
																}}
															>
																<Checkbox
																	size="small"
																	checked={
																		event.ifc_sent ===
																		true
																	}
																	disabled={
																		savingIfcByEvent[
																			String(
																				event.id,
																			)
																		] ===
																		true
																	}
																	onChange={(
																		e,
																	) =>
																		handleToggleIFCSent(
																			event,
																			e
																				.target
																				.checked,
																		)
																	}
																	sx={{
																		p: 0.5,
																		mr: 0.5,
																	}}
																/>

																<Typography variant="body2">
																	<strong>
																		IFC Sent
																	</strong>
																</Typography>

																{savingIfcByEvent[
																	String(
																		event.id,
																	)
																] && (
																	<CircularProgress
																		size={
																			14
																		}
																		sx={{
																			ml: 1,
																		}}
																	/>
																)}
															</Box>
														)}

														<Typography variant="body2">
															<strong>
																Actual Fee:
															</strong>{" "}
															{event?.actual_fee
																? `$${event?.actual_fee}`
																: "TBC"}
														</Typography>
													</Box>
												</Grid>

												<Grid item xs={12} md={2}>
													<Typography
														variant="body2"
														fontWeight="bold"
														gutterBottom
													>
														Item Numbers:
													</Typography>

													<Box
														sx={{
															display: "flex",
															flexDirection:
																"column",
															gap: 1,
															mb: 2,
														}}
													>
														{(
															sessionItemSets[
																event.id
															] || []
														).map((item, index) => (
															<Box
																key={index}
																sx={{
																	display:
																		"flex",
																	alignItems:
																		"center",
																	gap: 1,
																}}
															>
																<TextField
																	key={index}
																	value={item}
																	onChange={(
																		e,
																	) =>
																		handleItemChange(
																			event.id,
																			index,
																			e
																				.target
																				.value,
																		)
																	}
																	inputProps={{
																		style: {
																			paddingTop: 0,
																			paddingBottom: 0,
																		},
																	}}
																	sx={{
																		"& .MuiOutlinedInput-root":
																			{
																				pr: 0,
																				"& fieldset":
																					{
																						borderColor:
																							item ===
																							""
																								? "grey.400"
																								: validItemNumbers.has(
																											item,
																									  )
																									? "success.main"
																									: "error.main",
																						borderWidth: 1,
																					},
																				"&:hover fieldset":
																					{
																						borderColor:
																							item ===
																							""
																								? "grey.400"
																								: validItemNumbers.has(
																											item,
																									  )
																									? "success.main"
																									: "error.main",
																					},
																				"&.Mui-focused fieldset":
																					{
																						borderColor:
																							item ===
																							""
																								? "grey.400"
																								: validItemNumbers.has(
																											item,
																									  )
																									? "success.main"
																									: "error.main",
																					},
																			},
																		bgcolor:
																			"background.paper",
																	}}
																	size="small"
																	variant="outlined"
																	InputProps={{
																		endAdornment:
																			(
																				<IconButton
																					size="small"
																					color="error"
																					sx={{
																						padding: 0,
																					}}
																					onClick={() =>
																						handleDeleteItem(
																							event.id,
																							index,
																						)
																					}
																				>
																					<DeleteIcon />
																				</IconButton>
																			),
																	}}
																/>

																<IconButton
																	onClick={async () => {
																		try {
																			const text =
																				await navigator.clipboard.readText();

																			if (
																				text?.trim()
																			) {
																				handleItemChange(
																					event.id,
																					index,
																					text.trim(),
																				);
																			}
																		} catch (err) {
																			console.error(
																				"Failed to paste:",
																				err,
																			);
																		}
																	}}
																	size="small"
																>
																	<ContentPasteIcon fontSize="small" />
																</IconButton>
															</Box>
														))}

														<Box
															key="new-item-input"
															sx={{
																display: "flex",
																alignItems:
																	"center",
																gap: 1,
															}}
														>
															<TextField
																value={
																	newItems[
																		event.id
																	] || ""
																}
																onChange={(e) =>
																	setNewItems(
																		{
																			...newItems,
																			[event.id]:
																				e
																					.target
																					.value,
																		},
																	)
																}
																onKeyDown={(
																	e,
																) => {
																	if (
																		e.key ===
																		"Enter"
																	) {
																		handleAddItem(
																			event.id,
																		);
																	}
																}}
																placeholder="Add"
																inputProps={{
																	style: {
																		paddingTop: 0,
																		paddingBottom: 0,
																	},
																}}
																sx={{
																	"& .MuiOutlinedInput-root":
																		{
																			pr: 0,
																		},
																	bgcolor:
																		"background.paper",
																}}
																size="small"
																variant="outlined"
																InputProps={{
																	endAdornment:
																		(
																			<IconButton
																				size="small"
																				color="primary"
																				sx={{
																					padding: 0,
																				}}
																				onClick={() =>
																					handleAddItem(
																						event.id,
																					)
																				}
																			>
																				<AddCircleIcon />
																			</IconButton>
																		),
																}}
															/>

															<IconButton
																onClick={async () => {
																	try {
																		const text =
																			await navigator.clipboard.readText();

																		if (
																			text?.trim()
																		) {
																			setNewItems(
																				(
																					prev,
																				) => ({
																					...prev,
																					[event.id]:
																						text.trim(),
																				}),
																			);
																		}
																	} catch (err) {
																		console.error(
																			"Failed to paste:",
																			err,
																		);
																	}
																}}
																size="small"
															>
																<ContentPasteIcon fontSize="small" />
															</IconButton>
														</Box>
													</Box>
												</Grid>

												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														gap: 0.5,
														mr: 0,
														ml: "auto",
													}}
												>
													<Button
														disableElevation
														variant="contained"
														color="primary"
														size="small"
														onClick={() =>
															handleValidateAndSaveItems(
																event.id,
															)
														}
														disabled={
															!!savingStates[
																event.id
															]
														}
														sx={{
															minWidth: 0,
															mt: 2,
															mb: "auto",
															px: 1.2,
															py: 0.5,
															fontSize: "0.9rem",
															lineHeight: 1.1,
															textTransform:
																"none",
															whiteSpace:
																"nowrap",
														}}
														startIcon={
															savingStates[
																event.id
															] ? (
																<CircularProgress
																	size={10}
																	color="inherit"
																	sx={{
																		mr: 0.5,
																	}}
																/>
															) : null
														}
													>
														{savingStates[event.id]
															? "Saving..."
															: "Validate & Save"}
													</Button>

													{hasItemChanges(event.id) &&
														!savingStates[
															event.id
														] && (
															<Button
																disableElevation
																variant="contained"
																size="small"
																color="warning"
																onClick={() =>
																	handleCancelChanges(
																		event.id,
																	)
																}
																sx={{
																	minWidth: 0,
																	mb: 0,
																	mt: "auto",
																	px: 1,
																	py: 0.5,
																	fontSize:
																		"0.9rem",
																	lineHeight: 1.1,
																	textTransform:
																		"none",
																	whiteSpace:
																		"nowrap",
																}}
															>
																Cancel Changes
															</Button>
														)}
												</Box>
											</Grid>
										</AccordionDetails>
									</Accordion>

									<Button
										onClick={() => {
											dispatch(setSelectedEvent(event));

											dispatch(
												setSelectedEntityType("event"),
											);
										}}
										sx={{
											minWidth: 48,
											borderRadius: "0",
											borderLeft: "none",
											borderBottom: "1px solid #ccc",
											backgroundColor: "grey.200",
											color: "text.primary",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											"&:hover": {
												backgroundColor: "grey.300",
											},
										}}
									>
										<ArrowForwardIcon fontSize="small" />
									</Button>
								</Box>
							);
						})}
				</Box>
			</Box>
		</LocalizationProvider>
	);
};

export default SessionView;

const toDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return value;

	const parsedDate = new Date(value);

	return isNaN(parsedDate.getTime()) ? null : parsedDate;
};
